import React, { useState, useEffect, useRef } from "react";
import {
  Label,
  Input,
  TextArea,
  Checkbox,
  ControlError,
  CardHeader,
  CardContent,
  Card,
  Table,
  TableHead,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Select,
  SelectOption,
  RadioButton,
  SearchIcon,
  TextOverflow,
  ContentLoader,
} from "@forge/common";
import { useDispatch, useSelector } from "react-redux";
import { CREATE_COURSE_CONSTANTS } from "../utils/constants";
import {
  IVAPPVMTable,
  IVAPPVMTableHeader,
  ExistingTemplateProps,
} from "../model/index";
import { AppState } from "../../../../../common/store/combinedReducer";
import "./vm-template.scss";
import "../../create-course/create-course.scss";
import { getAllvAppTemplatesAndVms } from "../store/create-course.action";
import { throttle } from "lodash";

const NewVMFromExistingTemplate = ({
  vmFormData,
  vmFormError,
  handleVMFormChange,
  onTemplateSelect,
}: ExistingTemplateProps) => {
  const dispatch = useDispatch();
  const [tableData, setTableData] = useState<IVAPPVMTable[]>([]);
  const [filterType, setFilterType] = useState<string>("Template Name");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedVmsAndVapps, setSelectedVmsAndVapps] =
    useState<IVAPPVMTable | null>(null);
  const [filteredTableData, setFilteredTableData] = useState<IVAPPVMTable[]>(
    []
  );
  const [page, setPage] = useState(1);
  const hasReachedEnd = useRef<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const tableHeaders: IVAPPVMTableHeader[] = [
    { id: "vAppTemplate", name: "VAPP TEMPLATE NAME" },
    { id: "name", name: "NAME" },
    { id: "osType", name: "OS TYPE" },
    { id: "osVersion", name: "OS VERSION" },
    { id: "ram", name: "RAM" },
    { id: "cpu", name: "CPU" },
    { id: "vmPausable", name: "VM PAUSABLE" },
    { id: "ipAddress", name: "IP ADDRESS" },
    { id: "storage", name: "STORAGE" },
  ];
  const isTableLoading: any = useSelector((state: AppState) => {
    return state.createCourseReducer.vAppTemplatesList.isTableLoading;
  });
  const templatesTableData: any = useSelector((state: AppState) => {
    return state.createCourseReducer.vAppTemplatesList.tableData || {};
  });

  const fetchData = async (page: number) => {
    dispatch(getAllvAppTemplatesAndVms(page, 10));
  };

  useEffect(() => {
    if (page > 1) {
      fetchData(page);
    }
  }, [page]);

  const handleScroll = throttle(() => {
    if (!containerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;

    if (isAtBottom && !hasReachedEnd.current) {
      const totalPages = Math.ceil(
        templatesTableData?.totalRecords / templatesTableData?.pageSize
      );
      if (templatesTableData?.pageNo < totalPages) {
        hasReachedEnd.current = true;
        setPage((prev) => prev + 1);
      }
    } else if (!isAtBottom && hasReachedEnd.current) {
      hasReachedEnd.current = false;
    }
  }, 1000);

  useEffect(() => {
    if (
      templatesTableData &&
      Array.isArray(templatesTableData.vAppTemplates) &&
      templatesTableData.vAppTemplates.length > 0
    ) {
      const transformedData = transformVAppTemplateData(
        templatesTableData.vAppTemplates
      );
      setTableData(transformedData);
      setFilteredTableData(transformedData);
    }
  }, [templatesTableData]);

  const transformVAppTemplateData = (apiResponse: any[]) => {
    return apiResponse.map((template: any, index: number) => {
      const vmDetails = template.vmDetails || {};
      return {
        id: vmDetails.vmId || String(index + 1),
        vAppTemplate: template.vAppTemplateName || "--",
        name: vmDetails.vmName || "--",
        osType: vmDetails.osType || "--",
        osVersion: vmDetails.osVer || "--",
        ram: `${vmDetails.ramValue} ${vmDetails.ramUnit}` || "--",
        ipAddress: vmDetails.ipAddress || "--",
        cpu: vmDetails.cpu ? `${vmDetails.cpu} Cores` : "--",
        storage: vmDetails.storage || "--",
        vmPausable: vmDetails.isPausable === "true" ? "Yes" : "No",
      };
    });
  };
  const handleFilterTypeChange = (e: any) => {
    const value = e?.target?.value;
    if (value) {
      setFilterType(value);
      filterData(value, searchQuery);
    }
  };
  const filterData = (type: string, query: string) => {
    if (!query || !query.trim()) {
      setFilteredTableData(tableData);
      return;
    }
    const lowercasedQuery = query.trim().toLowerCase();
    let filtered = [];
    switch (type) {
      case "Template Name":
        filtered = tableData.filter((item) =>
          item.vAppTemplate.toLowerCase().includes(lowercasedQuery)
        );
        break;
      case "OS Type":
        filtered = tableData.filter((item) =>
          item.osType.toLowerCase().includes(lowercasedQuery)
        );
        break;
      default:
        filtered = tableData;
    }
    setFilteredTableData(filtered);
  };
  const handleSearchChange = (e: any) => {
    const query = e.target.value || "";
    setSearchQuery(query);
    filterData(filterType, query);
  };
  const onItemSelect = (data: IVAPPVMTable) => {
    setSelectedVmsAndVapps(data);
    if (handleVMFormChange) {
      handleVMFormChange("existingVm", data.name);
    }
    if (onTemplateSelect) {
      onTemplateSelect(data);
    }
  };
  useEffect(() => {
    if (filteredTableData.length > 0 && !selectedVmsAndVapps) {
      const firstItem = filteredTableData[0];
      setSelectedVmsAndVapps(firstItem);
      if (handleVMFormChange) {
        handleVMFormChange("existingVm", filteredTableData[0].name);
      }
      if (onTemplateSelect) {
        onTemplateSelect(firstItem);
      }
    }
  }, [filteredTableData, selectedVmsAndVapps, handleVMFormChange]);
  return (
    <>
      <div className="flex gap-10 mt-4 b-top-1">
        <div className="mt-4 w-[413px]">
          <Label required className="createCourse-label mb-2">
            {CREATE_COURSE_CONSTANTS.CREATE_VM_MODAL.VM_NAME.LABEL}
          </Label>
          <Input
            id="new-vm-name"
            className={`createCourse-name-input-field ${
              !vmFormError.vmName ? "!border-none" : ""
            }`}
            data-testid="new-vm-name"
            size="regular"
            placeholder="VM Name"
            value={vmFormData.vmName}
            onChange={(e) => handleVMFormChange("vmName", e.target.value)}
            error={!!vmFormError.vmName}
            autoComplete="off"
          />
          {vmFormError.vmName === "" && (
            <div className="text-sm text-gray-400 mt-1 text-xs">
              Max 15 characters
            </div>
          )}
          {vmFormError.vmName && (
            <ControlError className="text-xs" showIcon={false}>
              {vmFormError.vmName}
            </ControlError>
          )}
          <div className="mt-8">
            <Checkbox
              id="auto-pause"
              className="new-vm-input"
              data-testid="vm-auto-pause"
              checked={vmFormData.vmAutoPause}
              onChange={(e) =>
                handleVMFormChange("vmAutoPause", e.target.checked)
              }
            >
              {CREATE_COURSE_CONSTANTS.CREATE_VM_MODAL.ALLOW_AUTO_PAUSE}
            </Checkbox>
          </div>
        </div>
        <div className="mt-4 w-[413px]">
          <Label className="createCourse-label mb-2">
            {CREATE_COURSE_CONSTANTS.CREATE_VM_MODAL.DESCRIPTION.LABEL}
          </Label>
          <TextArea
            id="vm-description"
            className={`createCourse-description-input-field no-resize ${
              !vmFormError.description ? "!border-none" : ""
            }`}
            data-testid="vm-description"
            placeholder="Description"
            rows={3}
            size="regular"
            value={vmFormData.description}
            onChange={(e) => handleVMFormChange("description", e.target.value)}
            error={vmFormError.description !== ""}
          />
          {vmFormError.description === "" && (
            <div className="text-sm text-gray-400 mt-1 text-xs">
              Max 250 characters
            </div>
          )}
          {vmFormError.description && (
            <ControlError className="text-xs" showIcon={false}>
              {vmFormError.description}
            </ControlError>
          )}
        </div>
      </div>
      <div className="mt-4 b-top-1">
        <Label className="text-sm mt-4 mb-2">
          {CREATE_COURSE_CONSTANTS.LIST_VM_LABEL}
        </Label>
        <div>
          <Card className="border-unset mt-4">
            <CardHeader>
              <div className="grid grid-cols-4 gap-4 items-center w-full">
                <div className="col-span-1 !p-0">
                  <Input
                    id="search-input"
                    data-testid="search-input"
                    className="search-template-input-field !w-[260px]"
                    placeholder={`Search by viewable ${
                      filterType === "Template Name"
                        ? "Template Name"
                        : "OS Type"
                    }`}
                    onChange={(e) => handleSearchChange(e)}
                    prefix={<SearchIcon strokeWidth={2} />}
                    size="regular"
                    autoComplete="off"
                  />
                </div>
                <div className="col-span-1 filterBy !p-0">
                  <Select
                    key={`filter-select-${filterType}`}
                    height={1}
                    className="text-sm font-normal !border-none !outline-none filter-by-vapp w-[370px]"
                    id="filter-tempName"
                    data-testid="filter-tempName"
                    onChange={(e) => handleFilterTypeChange(e)}
                    placeholder={
                      filterType === "Template Name"
                        ? "Template Name"
                        : "OS Type"
                    }
                    value={filterType}
                    // defaultValue={filterType}
                    size="regular"
                  >
                    <SelectOption
                      label={"Template Name"}
                      value={"Template Name"}
                      className="text-sm font-normal"
                    />
                    <SelectOption
                      label={
                        CREATE_COURSE_CONSTANTS.VAPP_TEMPLATE_DETAILS.FILTER
                          .OPTIONS.OS_TYPE
                      }
                      value={"OS Type"}
                      className="text-sm font-normal"
                    />
                  </Select>
                </div>
                <div className="col-span-2 text-sm text-end !p-0">
                  <span>
                    {filteredTableData.length > 0
                      ? searchQuery.trim()
                        ? `${1}-${filteredTableData.length} of ${
                            filteredTableData.length
                          }` // When filtered
                        : `${1}-${filteredTableData.length} of ${
                            templatesTableData?.totalRecords || 0
                          }` // When not filtered
                      : "No results"}
                  </span>
                  {filterType && searchQuery.trim() && (
                    <>
                      <span className="mr-2 ml-2">|</span>
                      <span className="filter-applied">
                        {"Filter applied by " +
                          (filterType === "Template Name"
                            ? "Template Name"
                            : "OS Type")}
                      </span>
                    </>
                  )}
                  {(!filterType || !searchQuery.trim()) && (
                    <>
                      <span className="mr-2 ml-2">|</span>
                      <span className="filter-applied">
                        {"Filter applied by " +
                          (filterType === "Template Name"
                            ? "Template Name"
                            : "OS Type")}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </CardHeader>
            <div
              onScroll={handleScroll}
              ref={containerRef}
              className="vapp-temp-table-height"
            >
              <CardContent x="none" y="none">
                <Table onCard>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell
                        id="select-all-cell w-[70px]"
                        className="vpp-table-border"
                      ></TableHeaderCell>
                      {tableHeaders.map((header: IVAPPVMTableHeader) => (
                        <TableHeaderCell
                          className="text-sm vpp-table-border !max-w-[90px]"
                          key={header.id}
                          id={header.id}
                          data-testid={header.name}
                        >
                          {header.name}
                        </TableHeaderCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isTableLoading && (
                      <TableRow>
                        <TableCell
                          colSpan={10}
                          className="text-center !border-none"
                        >
                          <ContentLoader className="margin-xl-low margin-xl-mid margin-xl-high">
                            Loading
                          </ContentLoader>
                        </TableCell>
                      </TableRow>
                    )}
                    {filteredTableData.length > 0 &&
                      !isTableLoading &&
                      filteredTableData.map((data, i) => {
                        const isSelected = selectedVmsAndVapps?.id === data.id;
                        return (
                          <TableRow
                            id={`table-${i}`}
                            selected={isSelected}
                            key={i}
                          >
                            <TableCell
                              id={`select-id-${i}`}
                              className="table-body-cell !max-w-[70px]"
                            >
                              <RadioButton
                                className="text-sm cursor-pointer"
                                data-testid={`table-row-${i}-select`}
                                id={`vm-template-row-${i}-select`}
                                value={data.id}
                                name="vm-selection"
                                checked={isSelected}
                                onChange={() => onItemSelect(data)}
                              />
                            </TableCell>
                            <TableCell className="table-body-cell !max-w-[110px]">
                              <TextOverflow>{data.vAppTemplate}</TextOverflow>
                            </TableCell>
                            <TableCell className="table-body-cell !max-w-[110px]">
                              <TextOverflow>{data.name}</TextOverflow>
                            </TableCell>
                            <TableCell className="table-body-cell">
                              <TextOverflow>{data.osType}</TextOverflow>
                            </TableCell>
                            <TableCell className="table-body-cell !max-w-[110px]">
                              <TextOverflow>{data.osVersion}</TextOverflow>
                            </TableCell>
                            <TableCell className="table-body-cell">
                              <TextOverflow>{data.ram}</TextOverflow>
                            </TableCell>
                            <TableCell className="table-body-cell">
                              <TextOverflow>{data.cpu}</TextOverflow>
                            </TableCell>
                            <TableCell className="table-body-cell">
                              <TextOverflow>{data.vmPausable}</TextOverflow>
                            </TableCell>
                            <TableCell className="table-body-cell !max-w-[100px]">
                              <TextOverflow>{data.ipAddress}</TextOverflow>
                            </TableCell>
                            <TableCell className="table-body-cell">
                              <TextOverflow>{data.storage}</TextOverflow>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    {filteredTableData.length === 0 && !isTableLoading && (
                      <TableRow>
                        <TableCell
                          colSpan={10}
                          className="text-center !border-none"
                        >
                          <div className="min-h-32">
                            <p className="text-center text-sm font-bold vpp-table-border">
                              No data found
                            </p>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </div>
          </Card>
        </div>
      </div>
    </>
  );
};

export default NewVMFromExistingTemplate;
